import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { PermissionFlags } from '../enums/permissionFlags.enum';
import { SecurityProfileEnum } from '../enums/securityProfile.enum';
import { findFirstReadableAiAnalystAdminTab, getAiAnalystAdminRouteConfig } from '../shared/constants/aiAnalystAdminRouteConfig.constants';
import { RolePermissionObjects } from '../shared/constants/rolePermissionObjects.constants';

@Injectable({ providedIn: 'root' })
export class AiAnalystScopeService {
    readonly canAccessAiAnalyst$: Observable<boolean>;
    readonly canUseAiAnalyst$: Observable<boolean>;
    readonly firstAdminTabRoute$: Observable<string | null>;

    constructor(private authService: AuthService, private permissionService: PermissionService) {
        const enabledChanged$ = this.authService.aiAnalystEnabledChanged$.pipe(startWith(undefined));
        this.canAccessAiAnalyst$ = enabledChanged$.pipe(
            map(() => this.canAccessAiAnalyst),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.canUseAiAnalyst$ = combineLatest([enabledChanged$, this.permissionService.isLoaded$]).pipe(
            map(() => this.canUseAiAnalyst),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.firstAdminTabRoute$ = combineLatest([enabledChanged$, this.permissionService.isLoaded$]).pipe(
            map(() => this.firstAdminTabRoute()),
            distinctUntilChanged(),
        );
    }

    get canAccessAiAnalyst(): boolean {
        return !!this.authService.getCurrentUser()?.isAiAnalystEnabled;
    }

    get canUseAiAnalyst(): boolean {
        if (!this.canAccessAiAnalyst) {
            return false;
        }
        return this.authService.getCurrentUser()?.userType === SecurityProfileEnum.SuperAdmin
            || this.permissionService.hasPermissionFlag(
                RolePermissionObjects.AiAnalystAccess,
                PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy);
    }

    get canReadReferenceLibrary(): boolean {
        return this.isSuperAdmin
            || (this.canAccessAiAnalyst && this.permissionService.hasPermissionFlag(
                RolePermissionObjects.IhReference,
                PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy));
    }

    get canCreateReferenceBooks(): boolean {
        return this.isSuperAdmin
            || (this.canAccessAiAnalyst && this.permissionService.hasPermissionFlag(
                RolePermissionObjects.IhReference,
                PermissionFlags.CreateMy | PermissionFlags.CreateHierarchy));
    }

    get canUploadReferenceBooks(): boolean {
        return this.isSuperAdmin
            || (this.canAccessAiAnalyst && this.permissionService.hasPermissionFlag(
                RolePermissionObjects.IhReference,
                PermissionFlags.CreateMy | PermissionFlags.CreateHierarchy | PermissionFlags.UpdateMy | PermissionFlags.UpdateHierarchy));
    }

    getAdminTabs(): { routerLink: string; title: string; objectId?: string }[] {
        const isAdminOrSuperAdmin = this.isSuperAdmin || this.authService.getCurrentUser()?.userType === SecurityProfileEnum.Admin;
        return getAiAnalystAdminRouteConfig(this.isSuperAdmin, this.canAccessAiAnalyst, this.canReadReferenceLibrary)
            .filter(tab => !tab.objectId || isAdminOrSuperAdmin);
    }

    firstAdminTabRoute(): string | null {
        return findFirstReadableAiAnalystAdminTab(this.getAdminTabs(), objectId => this.canReadAdminObject(objectId))?.routerLink ?? null;
    }

    canOpenAdminTab(routerLink: string): boolean {
        return this.getAdminTabs().some(tab => tab.routerLink === routerLink && (!tab.objectId || this.canReadAdminObject(tab.objectId)));
    }

    get needsAdminMenuEntry(): boolean {
        return this.firstAdminTabRoute() != null && !this.canUseAiAnalyst;
    }

    private canReadAdminObject(objectId: string): boolean {
        return this.permissionService.hasPermissionFlag(objectId, PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy);
    }

    private get isSuperAdmin(): boolean {
        return this.authService.getCurrentUser()?.userType === SecurityProfileEnum.SuperAdmin;
    }
}
